import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Spin,
  Statistic,
  Progress,
  Table,
  Breadcrumb,
  Segmented,
  DatePicker,
  theme,
} from "antd";
import dayjs from "dayjs";
import {
  ArrowLeftOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  RiseOutlined,
  FieldTimeOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ScatterChart,
  Scatter,
  ZAxis,
  Cell,
  ReferenceLine,
  Legend,
} from "recharts";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import PageLayout from "@components/pageLayout/PageLayout.react";

const { Title, Text } = Typography;

/** Format kW/kWh: >= 1000 → MW/MWh with 1 decimal */
function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: val.toFixed(2), suffix: unit };
}

// ============================================================================
// SYSTEM STATUS CARD (same as Dashboard)
// ============================================================================
function SystemStatusCard({ groupId }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ online: 0, offline: 0, warning: 0, total: 0 });

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);
      try {
        const authToken = localStorage.getItem("token");
        const response = await fetch(`/api/groups/${groupId}/status`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const result = await response.json();

        if (result.success && result.data) {
          setStatus({
            online: result.data.online || 0,
            offline: result.data.offline || 0,
            warning: 0, // API doesn't return warning count yet
            total: result.data.total || 0,
          });
        }
      } catch (error) {
        console.error("Failed to fetch status:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [groupId]);

  const onlinePercent = status.total > 0 ? Math.round((status.online / status.total) * 100) : 0;

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, height: "100%" }}
      title={<Text strong style={{ fontSize: 16 }}>System Status</Text>}
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: 16 },
      }}
    >
      {loading ? (
        <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin />
        </div>
      ) : (
        <div>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <Progress
              type="dashboard"
              percent={onlinePercent}
              strokeColor={token.colorSuccess}
              size={120}
              format={(p) => (
                <div>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>{p}%</div>
                  <div style={{ fontSize: 11, color: token.colorTextSecondary }}>Online</div>
                </div>
              )}
            />
          </div>
          <Row gutter={8}>
            <Col span={8}>
              <div style={{ textAlign: "center", background: "rgba(5, 166, 119, 0.08)", padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: token.colorSuccess }}>{status.online}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>Online</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: "center", background: "rgba(250, 82, 82, 0.08)", padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: token.colorError }}>{status.offline}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>Offline</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: "center", background: "rgba(250, 173, 20, 0.08)", padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: token.colorWarning }}>{status.warning}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>Warning</Text>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// SYSTEM SCATTER PLOT (same as Dashboard)
// ============================================================================
function SystemScatterPlot({ groupId, onAvgSOC }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [systems, setSystems] = useState([]);

  useEffect(() => {
    const fetchSystems = async () => {
      setLoading(true);
      try {
        const authToken = localStorage.getItem("token");
        const response = await fetch(`/api/groups/${groupId}/systems`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const result = await response.json();

        if (result.success && result.data) {
          const mappedSystems = result.data.map((sys) => ({
            id: sys.sn || sys.id,
            inverterKw: sys.invKw || 0,
            soc: sys.soc != null ? Math.round(Number(sys.soc)) : 0,
            online: sys.online || false,
          }));
          setSystems(mappedSystems);
        }
      } catch (error) {
        console.error("Failed to fetch systems:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystems();
  }, [groupId]);

  const onlineCount = systems.filter((s) => s.online).length;
  const avgSOC = systems.length > 0 ? Math.round(systems.reduce((sum, s) => sum + s.soc, 0) / systems.length) : 0;

  useEffect(() => {
    if (onAvgSOC && systems.length > 0) onAvgSOC(avgSOC);
  }, [avgSOC]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    return (
      <div style={{ background: "#fff", padding: 8, borderRadius: 6, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", fontSize: 11 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.id}</div>
        <div>Inverter: <strong>{p.inverterKw} kW</strong></div>
        <div>SOC: <strong>{p.soc}%</strong></div>
        <Tag color={p.online ? "success" : "error"} style={{ marginTop: 4, fontSize: 10 }}>
          {p.online ? "Online" : "Offline"}
        </Tag>
      </div>
    );
  };

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, height: "100%" }}
      title={<Text strong style={{ fontSize: 16 }}>System Distribution</Text>}
      extra={
        <Space size={8}>
          <Tag color="success" style={{ fontSize: 10 }}>{onlineCount} Online</Tag>
          <Tag style={{ fontSize: 10 }}>Avg SOC: {avgSOC}%</Tag>
        </Space>
      }
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: 16 },
      }}
    >
      {loading ? (
        <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin />
        </div>
      ) : (
        <>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 10, bottom: 25, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                <XAxis
                  dataKey="inverterKw"
                  type="number"
                  name="Inverter"
                  domain={[0, 16]}
                  tick={{ fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  label={{ value: "Inverter (kW)", position: "bottom", offset: 5, fontSize: 10 }}
                />
                <YAxis
                  dataKey="soc"
                  type="number"
                  name="SOC"
                  domain={[0, 100]}
                  tick={{ fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                  label={{ value: "SOC %", angle: -90, position: "insideLeft", fontSize: 10 }}
                />
                <ZAxis range={[30, 30]} />
                <RechartsTooltip content={<CustomTooltip />} />
                <ReferenceLine y={20} stroke={token.colorError} strokeDasharray="3 3" />
                <ReferenceLine y={80} stroke={token.colorWarning} strokeDasharray="3 3" />
                <Scatter name="Systems" data={systems}>
                  {systems.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={
                        entry.online
                          ? entry.soc < 20
                            ? token.colorError
                            : entry.soc > 80
                            ? token.colorWarning
                            : token.colorSuccess
                          : token.colorTextDisabled
                      }
                      opacity={entry.online ? 0.8 : 0.4}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 8 }}>
            <Space size={4}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: token.colorSuccess }} />
              <Text type="secondary" style={{ fontSize: 10 }}>Normal (20-80%)</Text>
            </Space>
            <Space size={4}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: token.colorError }} />
              <Text type="secondary" style={{ fontSize: 10 }}>Low (&lt;20%)</Text>
            </Space>
            <Space size={4}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: token.colorWarning }} />
              <Text type="secondary" style={{ fontSize: 10 }}>High (&gt;80%)</Text>
            </Space>
            <Space size={4}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: token.colorTextDisabled }} />
              <Text type="secondary" style={{ fontSize: 10 }}>Offline</Text>
            </Space>
          </div>
        </>
      )}
    </Card>
  );
}

// ============================================================================
// SYSTEM PERFORMANCE CHART
// ============================================================================
function SystemPerformanceChart({ groupId, period, selectedDate, height = 240 }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ discharge: 0, vppDischarge: 0 });

  useEffect(() => {
    const fetchSnapshots = async () => {
      setLoading(true);
      try {
        const authToken = localStorage.getItem("token");
        const dateParam = selectedDate ? selectedDate.format("YYYY-MM-DD") : "";
        const response = await fetch(
          `/api/groups/${groupId}/snapshots?period=${period}&date=${dateParam}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        const result = await response.json();

        if (result.success && result.data) {
          const snapshots = result.data.snapshots || [];
          // Map snapshot data to chart format
          const chartData = snapshots.map((snap) => ({
            label: snap.label,
            discharge: Math.round(snap.discharge * 10) / 10,
            vppDischarge: Math.round(snap.vppDischarge * 10) / 10,
          }));
          setData(chartData);
          setTotals(result.data.totals || { discharge: 0, vppDischarge: 0 });
        }
      } catch (error) {
        console.error("Failed to fetch snapshots:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshots();
  }, [groupId, period, selectedDate]);

  const totalDischarge = Math.round(totals.discharge * 10) / 10;
  const totalVppDischarge = Math.round(totals.vppDischarge * 10) / 10;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Space size={16}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: token.colorPrimary }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Battery Discharge</Text>
            <Text strong style={{ fontSize: 14, color: token.colorPrimary }}>{totalDischarge.toFixed(1)} kWh</Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: "#f5a623" }} />
            <Text type="secondary" style={{ fontSize: 12 }}>VPP Dispatch</Text>
            <Text strong style={{ fontSize: 14, color: "#f5a623" }}>{totalVppDischarge.toFixed(1)} kWh</Text>
          </div>
        </Space>
      </div>
      <div style={{ position: "relative", height }}>
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.8)", zIndex: 10 }}>
            <Spin />
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: token.colorTextSecondary }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis yAxisId="left" tick={{ fontSize: 10, fill: token.colorTextSecondary }} axisLine={false} tickLine={false} width={40} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: token.colorTextSecondary }} axisLine={false} tickLine={false} width={40} />
            <RechartsTooltip
              formatter={(v, name) => [`${v} kWh`, name === "discharge" ? "Battery Discharge" : "VPP Dispatch"]}
            />
            <Bar yAxisId="left" dataKey="discharge" fill={token.colorPrimary} radius={[4, 4, 0, 0]} barSize={period === "day" ? 12 : 16} />
            <Bar yAxisId="right" dataKey="vppDischarge" fill="#f5a623" radius={[4, 4, 0, 0]} barSize={period === "day" ? 12 : 16} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// REVENUE CHART
// ============================================================================
function RevenueChart({ groupId, period, selectedDate, height = 240 }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ revenue: 0 });

  useEffect(() => {
    const fetchSnapshots = async () => {
      setLoading(true);
      try {
        const authToken = localStorage.getItem("token");
        const dateParam = selectedDate ? selectedDate.format("YYYY-MM-DD") : "";
        const response = await fetch(
          `/api/groups/${groupId}/snapshots?period=${period}&date=${dateParam}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        const result = await response.json();

        if (result.success && result.data) {
          const snapshots = result.data.snapshots || [];
          // Map snapshot data to chart format
          const chartData = snapshots.map((snap) => ({
            label: snap.label,
            revenue: Math.round(snap.revenue * 100) / 100,
          }));
          setData(chartData);
          setTotals(result.data.totals || { revenue: 0 });
        }
      } catch (error) {
        console.error("Failed to fetch revenue snapshots:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshots();
  }, [groupId, period, selectedDate]);

  const totalRevenue = Math.round(totals.revenue * 100) / 100;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: token.colorSuccess }} />
          <Text type="secondary" style={{ fontSize: 12 }}>Total Revenue</Text>
          <Text strong style={{ fontSize: 14, color: token.colorSuccess }}>${totalRevenue.toFixed(2)}</Text>
        </div>
      </div>
      <div style={{ position: "relative", height }}>
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.8)", zIndex: 10 }}>
            <Spin />
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={token.colorSuccess} stopOpacity={0.3} />
                <stop offset="95%" stopColor={token.colorSuccess} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: token.colorTextSecondary }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: token.colorTextSecondary }}
              axisLine={false}
              tickLine={false}
              width={50}
              tickFormatter={(v) => `$${v}`}
            />
            <RechartsTooltip
              formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]}
            />
            <Area type="monotone" dataKey="revenue" stroke={token.colorSuccess} strokeWidth={2} fill="url(#revenueGradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// SYSTEM LOCATION MAP
// ============================================================================

// Custom marker icons for different statuses
const createMarkerIcon = (color) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      width: 12px;
      height: 12px;
      background: ${color};
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    popupAnchor: [0, -6],
  });
};

const STATUS_COLORS = {
  running: "#05a677",   // green
  offline: "#6b7280",   // gray
  error: "#dc2626",     // red
  performance: "#faad14", // yellow/orange
};

const STATUS_ICONS = {
  running: createMarkerIcon(STATUS_COLORS.running),
  offline: createMarkerIcon(STATUS_COLORS.offline),
  error: createMarkerIcon(STATUS_COLORS.error),
  performance: createMarkerIcon(STATUS_COLORS.performance),
};

function SystemLocationMap({ groupId }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [systems, setSystems] = useState([]);
  const [totalSystemCount, setTotalSystemCount] = useState(0);

  useEffect(() => {
    const fetchSystems = async () => {
      setLoading(true);
      try {
        const authToken = localStorage.getItem("token");
        const response = await fetch(`/api/groups/${groupId}/systems`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const result = await response.json();

        if (result.success && result.data) {
          setTotalSystemCount(result.data.length);

          const mappedSystems = result.data
            .filter(sys => sys.latitude && sys.longitude) // Only include systems with coordinates
            .map((sys, i) => {
              const isOnline = sys.online || false;
              let status = isOnline ? "running" : "offline";

              return {
                id: sys.id,
                sn: sys.sn || `SYS${String(i + 1).padStart(3, "0")}`,
                lat: parseFloat(sys.latitude),
                lng: parseFloat(sys.longitude),
                status,
                inverterKw: sys.invKw || 0,
                batteryKwh: sys.batteryKwh || 0,
                suburb: sys.suburb || null,
                state: sys.state || null,
                addressline: sys.addressline || null,
                realtimePower: isOnline ? Math.round((Math.random() - 0.3) * (sys.invKw || 5) * 10) / 10 : 0,
              };
            });
          setSystems(mappedSystems);
        }
      } catch (error) {
        console.error("Failed to fetch systems for map:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystems();
  }, [groupId]);

  // Calculate map center and bounds
  const mapCenter = useMemo(() => {
    if (systems.length === 0) return [-33.8688, 151.2093];
    const avgLat = systems.reduce((sum, s) => sum + s.lat, 0) / systems.length;
    const avgLng = systems.reduce((sum, s) => sum + s.lng, 0) / systems.length;
    return [avgLat, avgLng];
  }, [systems]);

  const statusCounts = useMemo(() => {
    return systems.reduce((acc, s) => {
      acc[s.status] = (acc[s.status] || 0) + 1;
      return acc;
    }, {});
  }, [systems]);

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, marginTop: 16 }}
      title={
        <Space>
          <EnvironmentOutlined style={{ color: token.colorPrimary }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>System Locations</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ({systems.length} of {totalSystemCount} with coordinates)
          </Text>
        </Space>
      }
      extra={
        <Space size={12}>
          <Space size={4}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: STATUS_COLORS.running }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Running ({statusCounts.running || 0})</Text>
          </Space>
          <Space size={4}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: STATUS_COLORS.offline }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Offline ({statusCounts.offline || 0})</Text>
          </Space>
          <Space size={4}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: STATUS_COLORS.performance }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Performance ({statusCounts.performance || 0})</Text>
          </Space>
          <Space size={4}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: STATUS_COLORS.error }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Error ({statusCounts.error || 0})</Text>
          </Space>
        </Space>
      }
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: 0 },
      }}
    >
      {loading ? (
        <div style={{ height: 400, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin size="large" />
        </div>
      ) : systems.length === 0 ? (
        <div style={{ height: 400, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
          <EnvironmentOutlined style={{ fontSize: 48, color: token.colorTextDisabled }} />
          <Text type="secondary">
            {totalSystemCount > 0
              ? `${totalSystemCount} systems found but none have location coordinates.`
              : "No systems in this group."}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Run seed script with --clean to populate location data.
          </Text>
        </div>
      ) : (
        <div style={{ height: 400, borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
          <MapContainer
            center={mapCenter}
            zoom={11}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {systems.map((system) => (
              <Marker
                key={system.id}
                position={[system.lat, system.lng]}
                icon={STATUS_ICONS[system.status]}
              >
                <Popup>
                  <div style={{ minWidth: 140 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>{system.sn}</div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12 }}>
                      <span style={{ color: "#666" }}>Capacity:</span>
                      <span style={{ fontWeight: 500 }}>{system.inverterKw} kW / {system.batteryKwh} kWh</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                      <span style={{ color: "#666" }}>Power:</span>
                      <span style={{
                        fontWeight: 500,
                        color: system.realtimePower > 0 ? "#05a677" : system.realtimePower < 0 ? "#2563eb" : "#666"
                      }}>
                        {system.realtimePower > 0 ? "+" : ""}{system.realtimePower} kW
                        {system.realtimePower > 0 ? " (Discharge)" : system.realtimePower < 0 ? " (Charge)" : ""}
                      </span>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// MAIN GROUP DETAIL COMPONENT
// ============================================================================
export default function GroupDetail() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [chartPeriod, setChartPeriod] = useState("day");
  const [chartDate, setChartDate] = useState(dayjs());

  useEffect(() => {
    const fetchGroupDetail = async () => {
      setLoading(true);
      try {
        const authToken = localStorage.getItem("token");
        const response = await fetch(`/api/groups/${groupId}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data;
          setGroup({
            id: data.id,
            name: data.name,
            description: data.description,
            groupType: data.groupType,
            state: data.groupType === 'default' ? 'Default' : 'View',
            status: data.status,
            // Metadata
            sites: data.systems || 0,
            inverterKW: data.inverterKW || 0,
            batteryKWh: data.batteryKWh || 0,
            // Statistics
            totalGenerationKWh: 0, // TODO: Add to API if needed
            totalDischargeKWh: data.totalDischargeKWh || 0,
            totalRevenue: data.revenue || 0,
            // Additional info
            createdAt: data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : 'N/A',
            avgSOC: data.avgSoc || 0,
            onlineCount: data.onlineCount || 0,
            offlineCount: data.offlineCount || 0,
            companyName: data.companyName,
          });
        } else {
          console.error("Failed to fetch group:", result.message);
          setGroup(null);
        }
      } catch (error) {
        console.error("Failed to fetch group:", error);
        setGroup(null);
      } finally {
        setLoading(false);
      }
    };

    fetchGroupDetail();
  }, [groupId]);

  if (loading) {
    return (
      <PageLayout active="groupSummary">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!group) {
    return (
      <PageLayout active="groupSummary">
        <div style={{ textAlign: "center", padding: 48 }}>
          <Text type="secondary">Group not found</Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate("/group-management/groups")}>
              Back to Groups
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="groupSummary">
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate("/group-management")}>Group Management</a> },
          { title: <a onClick={() => navigate("/group-management/groups")}>Group Summary</a> },
          { title: group.name },
        ]}
      />

      {/* Header with back button */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/group-management/groups")}>
            Back
          </Button>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Title level={2} style={{ marginBottom: 0 }}>{group.name}</Title>
              <Tag color={group.status === "active" ? "success" : "default"}>
                {group.status === "active" ? "Active" : "Inactive"}
              </Tag>
            </div>
            <Text type="secondary" style={{ fontSize: 14 }}>
              <Tag color="blue" style={{ marginRight: 8 }}>{group.state}</Tag>
              Created: {group.createdAt}
            </Text>
          </div>
        </div>
      </div>

      {/* Row 1: Metadata Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 13 }}>Total Sites</Text>}
              value={group.sites}
              prefix={<HomeOutlined style={{ color: token.colorPrimary, marginRight: 8 }} />}
              valueStyle={{ fontSize: 28, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            {(() => { const f = formatPower(group.inverterKW || 0, "kW"); return (
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 13 }}>Inverter Capacity</Text>}
              value={f.value}
              suffix={f.suffix}
              prefix={<ThunderboltOutlined style={{ color: token.colorSuccess, marginRight: 8 }} />}
              valueStyle={{ fontSize: 28, fontWeight: 700 }}
            />); })()}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            {(() => { const f = formatPower(group.batteryKWh || 0, "kWh"); return (
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 13 }}>Battery Capacity</Text>}
              value={f.value}
              suffix={f.suffix}
              prefix={<BankOutlined style={{ color: "#f5a623", marginRight: 8 }} />}
              valueStyle={{ fontSize: 28, fontWeight: 700 }}
            />); })()}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 13 }}>Average SOC</Text>}
              value={group.avgSOC}
              suffix="%"
              prefix={<CheckCircleOutlined style={{ color: token.colorWarning, marginRight: 8 }} />}
              valueStyle={{ fontSize: 28, fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 2: Historical Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12, background: "linear-gradient(135deg, rgba(84, 162, 204, 0.08) 0%, rgba(84, 162, 204, 0.02) 100%)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(84, 162, 204, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <RiseOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Total Generation</Text>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{(group.totalGenerationKWh / 1000).toFixed(1)} <span style={{ fontSize: 14, fontWeight: 400 }}>MWh</span></div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12, background: "linear-gradient(135deg, rgba(5, 166, 119, 0.08) 0%, rgba(5, 166, 119, 0.02) 100%)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(5, 166, 119, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FieldTimeOutlined style={{ fontSize: 24, color: token.colorSuccess }} />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Total Discharge</Text>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{(group.totalDischargeKWh / 1000).toFixed(1)} <span style={{ fontSize: 14, fontWeight: 400 }}>MWh</span></div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12, background: "linear-gradient(135deg, rgba(250, 173, 20, 0.08) 0%, rgba(250, 173, 20, 0.02) 100%)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(250, 173, 20, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <DollarOutlined style={{ fontSize: 24, color: "#faad14" }} />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Total Revenue</Text>
                <div style={{ fontSize: 24, fontWeight: 700 }}>${group.totalRevenue.toLocaleString()}</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 3: System Status + System Distribution */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={10}>
          <SystemStatusCard groupId={groupId} />
        </Col>
        <Col xs={24} lg={14}>
          <SystemScatterPlot groupId={groupId} onAvgSOC={(val) => setGroup(g => g ? { ...g, avgSOC: val } : g)} />
        </Col>
      </Row>

      {/* Row 4: Performance & Revenue Charts - hidden for demo */}

      {/* Row 5: System Location Map */}
      <SystemLocationMap groupId={groupId} />
    </PageLayout>
  );
}
